package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.api.ChartPeriod
import org.zq.astrock_ai.ui.research.gushi.model.Candle
import org.zq.astrock_ai.ui.research.gushi.model.ChartRecommendMarker
import kotlin.math.abs

fun recommendMarkersFor(
    entries: List<RecommendHistorySnapshot>,
    code: String,
): List<ChartRecommendMarker> {
    val normalized = code.filter { it.isDigit() }.takeLast(6)
    if (normalized.length != 6) return emptyList()
    return entries.asSequence()
        .flatMap { snap ->
            snap.stocks.asSequence()
                .filter { stock ->
                    stock.price > 0 &&
                        stock.code.filter { it.isDigit() }.takeLast(6) == normalized
                }
                .map { stock ->
                    ChartRecommendMarker(
                        atMs = snap.atMs,
                        price = stock.price,
                        dateLabel = formatRecommendHistoryTime(snap.atMs),
                    )
                }
        }
        .sortedBy { it.atMs }
        .toList()
        .takeLast(30)
}

fun matchRecommendCandleIndex(
    candles: List<Candle>,
    atMs: Long,
    period: ChartPeriod,
): Int? {
    if (candles.isEmpty() || atMs <= 0L) return null
    val recDay = chinaEpochDay(atMs)
    return when (period) {
        ChartPeriod.INTRADAY, ChartPeriod.FIVE_DAY -> matchClosestTime(candles, atMs)
        ChartPeriod.DAY -> matchDayBar(candles, recDay)
        ChartPeriod.WEEK -> matchNearestDay(candles, recDay, maxDelta = 6)
        ChartPeriod.MONTH -> matchMonthBar(candles, atMs)
    }
}

fun parseCandleToEpochMs(date: String): Long? {
    val nums = date.trim().filter { it.isDigit() }
    if (nums.length < 8) return null
    val year = nums.substring(0, 4).toIntOrNull() ?: return null
    val month = nums.substring(4, 6).toIntOrNull() ?: return null
    val day = nums.substring(6, 8).toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31) return null
    val hour = if (nums.length >= 10) nums.substring(8, 10).toIntOrNull() ?: 0 else 0
    val minute = if (nums.length >= 12) nums.substring(10, 12).toIntOrNull() ?: 0 else 0
    return chinaCivilToEpochMs(year, month, day, hour, minute)
}

private fun matchClosestTime(candles: List<Candle>, atMs: Long): Int? {
    val stamped = candles.mapIndexedNotNull { index, candle ->
        val t = parseCandleToEpochMs(candle.date) ?: return@mapIndexedNotNull null
        index to t
    }
    if (stamped.isEmpty()) return null
    val first = stamped.first().second
    val last = stamped.last().second
    val lo = minOf(first, last)
    val hi = maxOf(first, last)
    if (atMs < lo - 60_000L) return null
    if (atMs > hi) {
        return if (chinaEpochDay(atMs) == chinaEpochDay(hi)) stamped.last().first else null
    }
    return stamped.minByOrNull { abs(it.second - atMs) }?.first
}

private fun matchDayBar(candles: List<Candle>, recDay: Int): Int? {
    val days = candles.mapIndexedNotNull { index, candle ->
        val t = parseCandleToEpochMs(candle.date) ?: return@mapIndexedNotNull null
        index to chinaEpochDay(t)
    }
    if (days.isEmpty()) return null
    days.firstOrNull { it.second == recDay }?.let { return it.first }
    val last = days.last()
    return if (recDay >= last.second && recDay - last.second <= 1) last.first else null
}

private fun matchNearestDay(candles: List<Candle>, recDay: Int, maxDelta: Int): Int? {
    val days = candles.mapIndexedNotNull { index, candle ->
        val t = parseCandleToEpochMs(candle.date) ?: return@mapIndexedNotNull null
        index to chinaEpochDay(t)
    }
    if (days.isEmpty()) return null
    return days
        .filter { abs(it.second - recDay) <= maxDelta }
        .minByOrNull { abs(it.second - recDay) }
        ?.first
}

private fun matchMonthBar(candles: List<Candle>, atMs: Long): Int? {
    val recYm = chinaYearMonth(atMs) ?: return null
    val months = candles.mapIndexedNotNull { index, candle ->
        val t = parseCandleToEpochMs(candle.date) ?: return@mapIndexedNotNull null
        val ym = chinaYearMonth(t) ?: return@mapIndexedNotNull null
        index to ym
    }
    if (months.isEmpty()) return null
    months.firstOrNull { it.second == recYm }?.let { return it.first }
    val last = months.last()
    return if (recYm.first == last.second.first && recYm.second >= last.second.second) last.first else null
}

private fun chinaYearMonth(atMs: Long): Pair<Int, Int>? {
    if (atMs <= 0L) return null
    val (year, month, _) = chinaYearMonthDay(atMs)
    return year to month
}
